import type { ButtonSize, ButtonTone, ButtonVariant } from '@strivelabs/ui-core/v2';

export type ContractButtonStyle = {
  variant: ButtonVariant;
  tone: ButtonTone;
  size: ButtonSize;
};

/**
 * Streamed UI blocks (confirmation actions, pills) carry button looks as plain
 * strings in the v1 vocabulary — `"ai-primary"`, `"outline"`, `"destructive"` —
 * because the server and the model write them as data. The strings stay the
 * contract; this maps each onto the button system so a block from any era
 * renders as a Primary, Secondary or Borderless pill. Names not listed here
 * (`"outline"`, `"ai-secondary"`, `"white"`, …) are all Secondary, which is
 * also the fallback for a payload we did not anticipate.
 */
const VARIANT: Record<string, { variant: ButtonVariant; tone: ButtonTone }> = {
  default: { variant: 'primary', tone: 'neutral' },
  primary: { variant: 'primary', tone: 'neutral' },
  black: { variant: 'primary', tone: 'neutral' },
  green: { variant: 'primary', tone: 'neutral' },
  'ai-primary': { variant: 'primary', tone: 'neutral' },
  destructive: { variant: 'primary', tone: 'danger' },
  red: { variant: 'secondary', tone: 'danger' },
  ghost: { variant: 'borderless', tone: 'neutral' },
  link: { variant: 'borderless', tone: 'neutral' },
  borderless: { variant: 'borderless', tone: 'neutral' },
};

const SIZE: Record<string, ButtonSize> = {
  xs: 'xs',
  sm: 'sm',
  compact: 'sm',
  icon: 'sm',
  md: 'md',
  default: 'md',
  lg: 'md',
};

export function contractButtonStyle({
  variant,
  size,
  colorVariant,
}: {
  variant: string;
  size: string;
  /** A pill's severity colour. The system has one semantic colour, so only `red` (danger) carries over. */
  colorVariant?: string;
}): ContractButtonStyle {
  const style = VARIANT[variant] ?? { variant: 'secondary', tone: 'neutral' };
  return {
    ...style,
    tone: colorVariant === 'red' ? 'danger' : style.tone,
    size: SIZE[size] ?? 'md',
  };
}
